/**
 * Primitives statistiques. Fonctions pures, sans dependance : c'est ce qui
 * permet de les tester contre des valeurs de reference connues.
 */

// ---------------------------------------------------------------------------
// Log-factorielles (cache croissant)
// ---------------------------------------------------------------------------

const logFactCache: number[] = [0, 0]

export function logFactorial(n: number): number {
  if (n < 0 || !Number.isFinite(n)) return NaN
  const k = Math.round(n)
  for (let i = logFactCache.length; i <= k; i++) {
    logFactCache[i] = logFactCache[i - 1]! + Math.log(i)
  }
  return logFactCache[k]!
}

function logChoose(n: number, k: number): number {
  if (k < 0 || k > n) return -Infinity
  return logFactorial(n) - logFactorial(k) - logFactorial(n - k)
}

// ---------------------------------------------------------------------------
// Test exact de Fisher
// ---------------------------------------------------------------------------

/**
 * Probabilite hypergeometrique (en log) d'observer `x` en case (1,1)
 * a marges fixees.
 */
function logProbHypergeom(x: number, r1: number, r2: number, k1: number): number {
  return logChoose(r1, x) + logChoose(r2, k1 - x) - logChoose(r1 + r2, k1)
}

/**
 * Test exact de Fisher bilateral sur la table `[[a, b], [c, d]]`.
 *
 * Retenu plutot que le khi-deux parce que les effectifs seront petits : sur un
 * journal de quelques mois, un ingredient donne n'apparait souvent que dans une
 * dizaine de repas, ou l'approximation asymptotique du khi-deux n'est plus valable.
 *
 * Methode : somme des probabilites de toutes les tables, a marges fixees, dont
 * la probabilite est inferieure ou egale a celle de la table observee.
 */
export function fisherExactBilateral(a: number, b: number, c: number, d: number): number {
  const r1 = a + b
  const r2 = c + d
  const k1 = a + c
  const n = r1 + r2
  if (n === 0 || r1 === 0 || r2 === 0 || k1 === 0 || k1 === n) return 1

  const lo = Math.max(0, k1 - r2)
  const hi = Math.min(r1, k1)
  const logObs = logProbHypergeom(a, r1, r2, k1)
  // Tolerance relative : deux tables de probabilite theoriquement egale peuvent
  // differer au dernier bit apres les logarithmes.
  const tol = 1e-9

  let p = 0
  for (let x = lo; x <= hi; x++) {
    const lp = logProbHypergeom(x, r1, r2, k1)
    if (lp <= logObs + tol) p += Math.exp(lp)
  }
  return Math.min(1, p)
}

// ---------------------------------------------------------------------------
// Intervalle de Wilson
// ---------------------------------------------------------------------------

export interface Intervalle {
  bas: number
  haut: number
}

/**
 * Intervalle de confiance de Wilson pour une proportion `succes / total`.
 *
 * Prefere a l'intervalle de Wald, qui degenere (bornes hors [0,1], largeur
 * nulle quand la proportion vaut 0 ou 1) precisement dans les cas frequents ici :
 * petits effectifs et taux extremes.
 */
export function intervalleWilson(succes: number, total: number, z = 1.96): Intervalle {
  if (total <= 0) return { bas: 0, haut: 1 }
  const p = succes / total
  const z2 = z * z
  const denom = 1 + z2 / total
  const centre = (p + z2 / (2 * total)) / denom
  const demi =
    (z / denom) * Math.sqrt((p * (1 - p)) / total + z2 / (4 * total * total))
  return {
    bas: Math.max(0, centre - demi),
    haut: Math.min(1, centre + demi),
  }
}

// ---------------------------------------------------------------------------
// Correction des tests multiples
// ---------------------------------------------------------------------------

/**
 * Benjamini-Hochberg : convertit une famille de p-values en q-values
 * (taux de fausses decouvertes attendu).
 *
 * Indispensable ici : avec ~200 ingredients x 9 symptomes, tester a p < 0,05
 * produirait en moyenne une centaine de « declencheurs » sur du bruit pur.
 *
 * Retourne les q-values dans l'ordre d'entree.
 */
export function benjaminiHochberg(pvalues: readonly number[]): number[] {
  const m = pvalues.length
  if (m === 0) return []

  const ordre = pvalues
    .map((p, i) => ({ p, i }))
    .sort((x, y) => x.p - y.p)

  const q = new Array<number>(m)
  let minSuivant = 1
  // Parcours descendant : q_i = min( q_{i+1}, p_i * m / rang_i )
  for (let rang = m; rang >= 1; rang--) {
    const item = ordre[rang - 1]!
    const brut = (item.p * m) / rang
    minSuivant = Math.min(minSuivant, brut)
    q[item.i] = Math.min(1, minSuivant)
  }
  return q
}

// ---------------------------------------------------------------------------
// Loi normale
// ---------------------------------------------------------------------------

/** Fonction de repartition de la loi normale centree reduite (Zelen & Severo, erreur < 8e-8). */
export function normaleCdf(z: number): number {
  const signe = z < 0 ? -1 : 1
  const x = Math.abs(z)
  const t = 1 / (1 + 0.2316419 * x)
  const poly =
    t *
    (0.319381530 +
      t * (-0.356563782 + t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))))
  const densite = Math.exp((-x * x) / 2) / Math.sqrt(2 * Math.PI)
  const queue = densite * poly
  return signe > 0 ? 1 - queue : queue
}

// ---------------------------------------------------------------------------
// Mann-Whitney U
// ---------------------------------------------------------------------------

export interface ResultatMannWhitney {
  u: number
  p: number
  /** Probabilite qu'un tirage de `x` depasse un tirage de `y` (taille d'effet, 0,5 = aucun effet). */
  auc: number
}

/**
 * Test U de Mann-Whitney bilateral (approximation normale, correction pour ex aequo).
 *
 * Utilise pour l'effet-dose : comparer la charge FODMAP des repas suivis d'un
 * declenchement a celle des autres. Non parametrique, car les charges sont tres
 * asymetriques (beaucoup de repas a charge nulle, quelques-uns tres eleves).
 */
export function mannWhitney(x: readonly number[], y: readonly number[]): ResultatMannWhitney {
  const n1 = x.length
  const n2 = y.length
  if (n1 === 0 || n2 === 0) return { u: 0, p: 1, auc: 0.5 }

  const tout = [
    ...x.map((v) => ({ v, g: 0 })),
    ...y.map((v) => ({ v, g: 1 })),
  ].sort((a, b) => a.v - b.v)

  // Rangs moyens sur les ex aequo + accumulation du terme de correction
  const rangs = new Array<number>(tout.length)
  let correctionTies = 0
  let i = 0
  while (i < tout.length) {
    let j = i
    while (j + 1 < tout.length && tout[j + 1]!.v === tout[i]!.v) j++
    const rangMoyen = (i + j) / 2 + 1
    for (let k = i; k <= j; k++) rangs[k] = rangMoyen
    const t = j - i + 1
    if (t > 1) correctionTies += t * t * t - t
    i = j + 1
  }

  let sommeRangs1 = 0
  for (let k = 0; k < tout.length; k++) {
    if (tout[k]!.g === 0) sommeRangs1 += rangs[k]!
  }

  const u1 = sommeRangs1 - (n1 * (n1 + 1)) / 2
  const n = n1 + n2
  const moyenne = (n1 * n2) / 2
  const varianceBase = (n1 * n2 * (n + 1)) / 12
  const variance = varianceBase - (n1 * n2 * correctionTies) / (12 * n * (n - 1))

  if (variance <= 0) return { u: u1, p: 1, auc: u1 / (n1 * n2) }

  // Correction de continuite
  const z = (Math.abs(u1 - moyenne) - 0.5) / Math.sqrt(variance)
  const p = Math.min(1, 2 * (1 - normaleCdf(Math.max(0, z))))

  return { u: u1, p, auc: u1 / (n1 * n2) }
}

// ---------------------------------------------------------------------------
// Descriptif
// ---------------------------------------------------------------------------

export function mediane(valeurs: readonly number[]): number {
  if (valeurs.length === 0) return NaN
  const t = [...valeurs].sort((a, b) => a - b)
  const m = t.length >> 1
  return t.length % 2 ? t[m]! : (t[m - 1]! + t[m]!) / 2
}

export function quantile(valeurs: readonly number[], q: number): number {
  if (valeurs.length === 0) return NaN
  const t = [...valeurs].sort((a, b) => a - b)
  const pos = (t.length - 1) * q
  const bas = Math.floor(pos)
  const haut = Math.ceil(pos)
  if (bas === haut) return t[bas]!
  return t[bas]! + (t[haut]! - t[bas]!) * (pos - bas)
}

// ---------------------------------------------------------------------------
// Generateur pseudo-aleatoire deterministe
// ---------------------------------------------------------------------------

/**
 * mulberry32 : rapide, de qualite suffisante pour un test de permutation, et
 * surtout **reproductible a graine fixee** — sans quoi les resultats d'analyse
 * changeraient a chaque affichage et les tests ne seraient pas deterministes.
 */
export function rng(graine: number): () => number {
  let a = graine >>> 0
  return function () {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), 1 | t)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}
